import { defineStore } from "pinia";
import { api } from "@/lib/api";
import { UsersEndpoint } from "@/lib/api/endpoints/UsersEndpoint";
import { queryClient } from "@/lib/query";
import { ssoErrorMessage } from "@/lib/domain/sso";
import { loginQuery, rememberSsoRedirect, safeRedirect } from "@/lib/domain/authRedirect";
import { z } from "zod";

type ProfileType = z.infer<typeof UsersEndpoint.schemas.profile.response>;

export const useAuthStore = defineStore("auth", {
  state: () => ({
    error: "",
    isLoading: false,
    profile: null as ProfileType | null,
  }),
  getters: {
    isAuthenticated: (state): boolean => {
      return !!state.profile && !!api.auth.token;
    },
    displayName: (state): string =>
      [state.profile?.first_name, state.profile?.last_name].filter(Boolean).join(" ") ||
      state.profile?.username ||
      "User",
    initials(): string {
      return this.displayName
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },
  },
  actions: {
    /** Sending the user to Login is the router guard's job; it knows where they were going. */
    async fetchProfile() {
      const profile = await api.users.profile();
      if (!profile.ok) return null;
      this.profile = profile.data;
      return this.profile;
    },
    /** `redirect` is Login's `?redirect=`; it is checked by {@link safeRedirect}. */
    async login(username: string, password: string, redirect?: unknown) {
      this.error = "";
      this.isLoading = true;
      const res = await api.users.login({ username, password });
      if (!res.ok) {
        this.error = res.error;
        this.isLoading = false;
        return;
      }

      if (!res.data.success) {
        this.error = res.data.message ?? "Unknown login error. Message not provided.";
        this.isLoading = false;
        return;
      }

      if (!res.data.token) {
        this.error = "No token found";
        this.isLoading = false;
        return;
      }

      api.auth.token = res.data.token;
      await this.fetchProfile();
      this.isLoading = false;
      await this.router.push(safeRedirect(redirect));
    },

    /** Redirect the browser to a provider's backend SSO entry point, which bounces to the IdP. */
    startSsoLogin(loginUrl: string, redirect?: unknown) {
      this.error = "";
      if (!loginUrl) {
        this.error = "SSO is not configured.";
        return;
      }
      rememberSsoRedirect(redirect);
      window.location.href = loginUrl;
    },

    /**
     * Surface a failed SSO round-trip on the login screen, which renders `error`.
     * The backend reports the reason as a code in the callback fragment.
     */
    failSsoLogin(code: string) {
      this.error = ssoErrorMessage(code);
    },

    /**
     * Finish an SSO round-trip: store the bearer token handed back on the callback
     * and load the profile. Returns whether the session was established.
     */
    async completeSsoLogin(token: string): Promise<boolean> {
      api.auth.token = token;
      const profile = await api.users.profile();
      if (!profile.ok) {
        api.removeAuthToken();
        this.profile = null;
        queryClient.clear();
        return false;
      }
      this.profile = profile.data;
      return true;
    },

    async initAuth() {
      if (!api.auth.token) throw new Error("No token found");
      if (!(await this.fetchProfile())) throw new Error("Profile could not be loaded");
    },
    /**
     * Move the account into another of its clients. Every project, run and cached
     * answer on screen belongs to the old one, so a successful switch reloads the
     * app from Home rather than trying to patch stores and caches in place.
     * Errors are already toasted by the API client; the caller only learns it failed.
     */
    async switchClient(clientId: number): Promise<boolean> {
      const res = await api.users.switchClient(clientId);
      if (!res.ok) return false;
      this.profile = res.data;
      window.location.assign("/");
      return true;
    },
    /** Drops the session on this side and sends the user to Login, to come back to `returnTo` once signed in. */
    async endSession(returnTo = "/") {
      api.auth.removeAuthToken();
      this.profile = null;
      queryClient.clear();
      await this.router.replace({ name: "Login", query: loginQuery(returnTo) });
    },
    async logout(returnTo = "/") {
      try {
        await api.users.logout();
      } finally {
        await this.endSession(returnTo);
      }
    },
  },
});
